import React from 'react';
import { Modal, Button, Row, Col, Table, Badge, Image } from 'react-bootstrap';
import { FaTimes } from 'react-icons/fa';
import { Company } from '../../../features/companies/companiesService';
import { formatDate } from '../../../utils/dateUtils';
import { Icon } from '../../../components/common/Icon';
import { resolveLogoUrl } from '../../../utils/urlUtils';
import { useTranslation } from 'react-i18next';

interface CompanyDetailModalProps {
  show: boolean;
  onHide: () => void;
  company: Company;
}

const CompanyDetailModal: React.FC<CompanyDetailModalProps> = ({ 
  show, 
  onHide, 
  company 
}) => {
  const { t } = useTranslation();
  const logoUrl = company.logo_url ? resolveLogoUrl(company.logo_url) : null;

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <div className="d-flex align-items-center gap-3">
          {logoUrl && (
            <Image
              src={logoUrl}
              alt={company.company_name}
              style={{ 
                width: '40px', 
                height: '40px', 
                objectFit: 'contain',
                borderRadius: '4px'
              }}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
          )}
          <Modal.Title>{t("company_details") || "Şirket Detayları"}</Modal.Title>
        </div>
      </Modal.Header>
      
      <Modal.Body>
        <Row className="mb-4">
          <Col xs={12} md={8}>
            <div className="d-flex align-items-center gap-3 mb-3">
              {logoUrl && (
                <Image
                  src={logoUrl}
                  alt={company.company_name}
                  thumbnail
                  style={{ 
                    width: '80px', 
                    height: '80px', 
                    objectFit: 'contain',
                    flexShrink: 0
                  }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              )}
              <div>
                <h4 className="mb-1">{company.company_name}</h4>
                <p className="text-muted mb-0">
                  {company.company_type === 'agency' 
                    ? t("agency") || 'Ajans' 
                    : company.company_type === 'client' 
                    ? t("client") || 'Müşteri' 
                    : t("single") || 'Tekil Şirket'}
                  {company.industry && ` • ${company.industry}`}
                </p>
              </div>
            </div>
            {company.description && <p>{company.description}</p>}
          </Col>
          <Col xs={12} md={4} className="text-md-end">
            <Badge bg={company.is_active ? 'success' : 'danger'}>
              {company.is_active ? (t("active") || 'Aktif') : (t("inactive") || 'Pasif')}
            </Badge>
          </Col>
        </Row>
        
        <Table striped bordered responsive>
          <tbody>
            {company.website && (
              <tr>
                <th style={{ width: '30%' }}>Website</th>
                <td>
                  <a href={company.website} target="_blank" rel="noopener noreferrer">
                    {company.website}
                  </a>
                </td>
              </tr>
            )}
            
            {company.email && (
              <tr>
                <th>E-posta</th>
                <td>
                  <a href={`mailto:${company.email}`}>{company.email}</a>
                </td>
              </tr>
            )}
            
            {company.phone && (
              <tr>
                <th>Telefon</th>
                <td>
                  <a href={`tel:${company.phone}`}>{company.phone}</a>
                </td>
              </tr>
            )}
            
            {company.address && (
              <tr>
                <th>Adres</th>
                <td>{company.address}</td>
              </tr>
            )}
            
            {(company.city || company.country) && (
              <tr>
                <th>Şehir/Ülke</th>
                <td>
                  {company.city && company.city}
                  {company.city && company.country && ', '}
                  {company.country && company.country}
                </td>
              </tr>
            )}
            
            {company.postal_code && (
              <tr>
                <th>Posta Kodu</th>
                <td>{company.postal_code}</td>
              </tr>
            )}
            
            {company.tax_id && (
              <tr>
                <th>Vergi No</th>
                <td>{company.tax_id}</td>
              </tr>
            )}
            
            {company.company_size && (
              <tr>
                <th>Şirket Büyüklüğü</th>
                <td>{company.company_size}</td>
              </tr>
            )}
            
            {company.annual_revenue && (
              <tr>
                <th>Yıllık Gelir</th>
                <td>{company.annual_revenue.toLocaleString('tr-TR')} ₺</td>
              </tr>
            )}
            
            {logoUrl && (
              <tr>
                <th>{t("logo") || "Logo"}</th>
                <td>
                  <Image
                    src={logoUrl}
                    alt={company.company_name}
                    thumbnail
                    style={{ 
                      maxWidth: '120px', 
                      maxHeight: '120px', 
                      objectFit: 'contain'
                    }}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                  />
                </td>
              </tr>
            )}
            
            {company.establishment_date && (
              <tr>
                <th>Kuruluş Tarihi</th>
                <td>{formatDate(company.establishment_date)}</td>
              </tr>
            )}
            
            <tr>
              <th>Oluşturulma Tarihi</th>
              <td>{formatDate(company.created_at)}</td>
            </tr>
            
            <tr>
              <th>Son Güncelleme</th>
              <td>{formatDate(company.updated_at)}</td>
            </tr>
          </tbody>
        </Table>
      </Modal.Body>
      
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          <Icon icon={FaTimes} /> {t("close") || "Kapat"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CompanyDetailModal;